import React from "react";
import { colors } from "./constants";
import {
  formatDate,
  getMonthGrid,
  isPast,
  isToday,
  isWeekend,
  MONTH_NAMES,
} from "./dateUtils";

// A Monday-first month calendar with a month stepper. The chrome comes from
// fieldtech/FieldTechRoster.js, generalised: the caller decides what a day
// looks like and what clicking one does.
//
// renderDay(dateStr, date) -> optional node drawn under the day number.
// dayStyle(dateStr, date)  -> optional style overrides for the cell.
export function MonthGrid({
  year,
  month,
  onMonthChange,
  selected,
  onDayClick,
  isDayDisabled,
  renderDay,
  dayStyle,
  minHeight = "52px",
}) {
  const { grid } = getMonthGrid(year, month);
  const selectedSet = selected instanceof Set ? selected : new Set(selected || []);

  const step = (delta) => {
    if (!onMonthChange) return;
    const d = new Date(year, month + delta, 1);
    onMonthChange(d.getFullYear(), d.getMonth());
  };

  const navButton = {
    background: "none",
    border: `1px solid ${colors.border}`,
    borderRadius: "6px",
    width: "28px",
    height: "28px",
    cursor: "pointer",
    color: colors.slate,
    fontSize: "14px",
    lineHeight: "1",
  };

  return (
    <div>
      {onMonthChange && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <button onClick={() => step(-1)} style={navButton} aria-label="Previous month">
            ‹
          </button>
          <div style={{ fontSize: "14px", fontWeight: "700", color: colors.text }}>
            {MONTH_NAMES[month]} {year}
          </div>
          <button onClick={() => step(1)} style={navButton} aria-label="Next month">
            ›
          </button>
        </div>
      )}

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            backgroundColor: colors.darkGrey,
          }}
        >
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <div
              key={i}
              style={{
                textAlign: "center",
                padding: "8px 0",
                fontSize: "11px",
                fontWeight: "700",
                color: i >= 5 ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.7)",
              }}
            >
              {d}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {grid.map((date, idx) => {
            if (!date)
              return (
                <div
                  key={idx}
                  style={{
                    minHeight,
                    backgroundColor: "#fafafa",
                    borderRight: `1px solid ${colors.border}`,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                />
              );

            const dateStr = formatDate(date);
            const disabled = isDayDisabled ? isDayDisabled(dateStr, date) : false;
            const isSelected = selectedSet.has(dateStr);
            const today = isToday(date);

            return (
              <div
                key={idx}
                onClick={() => !disabled && onDayClick && onDayClick(dateStr, date)}
                style={{
                  padding: "6px 4px",
                  minHeight,
                  backgroundColor: isSelected
                    ? "#e0f2fe"
                    : isWeekend(date)
                    ? "#fafafa"
                    : colors.white,
                  borderRight: `1px solid ${colors.border}`,
                  borderBottom: `1px solid ${colors.border}`,
                  cursor: disabled || !onDayClick ? "default" : "pointer",
                  opacity: disabled || isPast(date) ? 0.45 : 1,
                  outline: isSelected ? `2px solid ${colors.activeBlue}` : "none",
                  outlineOffset: "-2px",
                  ...(dayStyle ? dayStyle(dateStr, date) : {}),
                }}
              >
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    backgroundColor: today ? colors.teal : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 2px",
                    fontSize: "12px",
                    fontWeight: isSelected || today ? "700" : "500",
                    color: today ? colors.white : colors.text,
                  }}
                >
                  {date.getDate()}
                </div>
                {renderDay && renderDay(dateStr, date)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
